import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo, Brand } from "@/components/Logo";
import { ArrowRight, KeyRound, Eye, EyeOff, Loader2, ShieldX, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`)
      .then((r) => { setValid(r.data.valid); setEmail(r.data.email || ""); })
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated — you can sign in now");
      setTimeout(() => navigate("/auth"), 2500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-4 py-3 text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00] focus:ring-1 focus:ring-[#FF5A00] transition-colors";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] px-6"
      style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="w-full max-w-md" data-testid="reset-password-page">
        <div className="mb-8"><Logo size="text-3xl" /></div>

        {checking ? (
          <div className="flex items-center gap-3 text-[#9ca3af]" data-testid="reset-checking">
            <Loader2 size={20} className="text-[#FF5A00] animate-spin" /> Checking your link…
          </div>
        ) : !valid ? (
          <div data-testid="reset-invalid">
            <div className="w-12 h-12 rounded-full bg-[#f87171]/10 border border-[#f87171]/30 flex items-center justify-center mb-4">
              <ShieldX size={22} className="text-[#f87171]" />
            </div>
            <h2 className="font-display font-black text-3xl tracking-tight mb-2">Link expired or invalid</h2>
            <p className="text-[#9ca3af] mb-6">
              This password reset link has expired or was already used. Request a fresh one from the sign-in page.
            </p>
            <button data-testid="reset-goto-signin" onClick={() => navigate("/auth")}
              className="inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-6 py-3 hover:bg-[#FF7A33] transition-colors">
              Back to sign in <ArrowRight size={18} />
            </button>
          </div>
        ) : done ? (
          <div data-testid="reset-done">
            <div className="w-12 h-12 rounded-full bg-[#4ade80]/10 border border-[#4ade80]/30 flex items-center justify-center mb-4">
              <CheckCircle2 size={22} className="text-[#4ade80]" />
            </div>
            <h2 className="font-display font-black text-3xl tracking-tight mb-2">Password updated</h2>
            <p className="text-[#9ca3af]">Taking you to the sign-in page…</p>
          </div>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-3">
              <KeyRound size={14} /> Choose a new password
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-2">
              Reset your <Brand /> password
            </h2>
            {email && <p className="text-[#9ca3af] mb-8">for <span className="text-white font-semibold">{email}</span></p>}

            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div className="relative">
                <input data-testid="reset-new-password-input" type={show ? "text" : "password"} className={`${inputCls} pr-12`}
                  placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)}
                  required minLength={6} autoComplete="new-password" />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b7280] hover:text-white transition-colors">
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <input data-testid="reset-confirm-password-input" type={show ? "text" : "password"} className={inputCls}
                placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                required minLength={6} autoComplete="new-password" />

              <button data-testid="reset-submit-button" type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-6 py-3.5 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">
                {loading ? "Updating…" : "Update password"}
                {!loading && <ArrowRight size={18} />}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
